import { useEffect, useRef, useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Card, { SectionTitle } from "@/components/base/Card";
import Button from "@/components/base/Button";
import Tabs from "@/components/base/Tabs";
import { Input } from "@/components/base/Field";
import { LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { getSchoolInfo, updateSchoolInfo } from "@/services/schoolService";
import type { SchoolInfo } from "@/types";
import SchoolYearsPanel from "./components/SchoolYearsPanel";
import CurrencyFields from "./components/CurrencyFields";
import GradingPanel from "./components/GradingPanel";
import CyclesPanel from "./components/CyclesPanel";
import { useSearchParams } from "react-router-dom";
import { PLAN_LABEL, PLAN_SUMMARY, subscriptionState, SUBSCRIPTION_WARNING_DAYS } from "@/utils/plans";
import { plural } from "@/utils/format";

const baseTabs = [
  { id: "school", label: "École", icon: "ri-school-line" },
  { id: "cycles", label: "Cycles", icon: "ri-stack-line" },
  { id: "years", label: "Années scolaires", icon: "ri-calendar-2-line" },
];
const gradingTab = { id: "grading", label: "Notes et périodes", icon: "ri-edit-box-line" };

export default function SettingsPage() {
  const { notify } = useToast();
  const { refreshSchools, can } = useAuth();
  const showGrading = can("grading-periods:update") || can("grading-periods:validate");
  const tabs = showGrading ? [...baseTabs, gradingTab] : baseTabs;
  const [searchParams] = useSearchParams();
  const [active, setActive] = useState(() =>
    tabs.some((t) => t.id === searchParams.get("tab")) ? (searchParams.get("tab") as string) : "school",
  );
  const [info, setInfo] = useState<SchoolInfo | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getSchoolInfo().then(setInfo);
  }, []);

  if (!info) return <LoadingState />;

  const handleLogoPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      notify("Veuillez choisir un fichier image.", "error");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setInfo((prev) => (prev ? { ...prev, logo: String(reader.result) } : prev));
      notify("Nouveau logo chargé. Pensez à enregistrer.");
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <div className="animate-fade-in">
      <PageHeader title="Paramètres" subtitle="Identité de l'école, cycles, années scolaires et notes" icon="ri-settings-3-line" />

      <div className="mb-6">
        <Tabs tabs={tabs} active={active} onChange={setActive} />
      </div>

      <div className="max-w-3xl space-y-5">
        {active === "school" && (
          <>
          <Card>
            <SectionTitle icon="ri-school-line" title="Établissement" subtitle="Identité et coordonnées" />

            <div className="mb-5 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100/50 p-4 sm:flex-row sm:items-center">
              <span className="w-20 h-20 shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-50 flex items-center justify-center">
                {info.logo ? (
                  <img src={info.logo} alt="Logo de l'établissement" title={info.name} className="w-full h-full object-contain" />
                ) : (
                  <i className="ri-image-add-line text-2xl text-foreground-400"></i>
                )}
              </span>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground-900">Logo de l'école</p>
                <p className="text-xs text-foreground-500 mt-0.5">Image carrée recommandée (PNG ou JPG). Affiché sur les documents officiels et les reçus.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoPick} />
                  <Button size="sm" variant="secondary" icon="ri-upload-2-line" onClick={() => logoInputRef.current?.click()}>
                    Changer le logo
                  </Button>
                  {info.logo && (
                    <Button size="sm" variant="ghost" icon="ri-delete-bin-line" onClick={() => setInfo({ ...info, logo: "" })}>
                      Retirer
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input label="Nom" value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} />
              <Input label="Téléphone" value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} />
              <Input label="Ville" value={info.city} onChange={(e) => setInfo({ ...info, city: e.target.value })} />
              <Input label="Email" value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} />
              <Input label="Adresse" value={info.address} onChange={(e) => setInfo({ ...info, address: e.target.value })} wrapperClass="sm:col-span-2" />
              <Input label="Slogan" value={info.slogan} onChange={(e) => setInfo({ ...info, slogan: e.target.value })} wrapperClass="sm:col-span-2" />
            </div>

            <p className="mt-6 mb-1 text-sm font-medium text-foreground-900">En-tête des cartes scolaires</p>
            <p className="mb-3 text-xs text-foreground-500">Imprimé en haut de chaque carte. Laissez une ligne vide pour ne pas l'imprimer.</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="État"
                placeholder="République de Guinée"
                value={info.stateName}
                onChange={(e) => setInfo({ ...info, stateName: e.target.value })}
              />
              <Input
                label="Devise nationale"
                placeholder="Travail – Justice – Solidarité"
                value={info.stateMotto}
                onChange={(e) => setInfo({ ...info, stateMotto: e.target.value })}
              />
              <Input
                label="Ministère"
                placeholder="Ministère de l’Enseignement Pré-Universitaire et de l’Alphabétisation"
                value={info.ministryName}
                onChange={(e) => setInfo({ ...info, ministryName: e.target.value })}
                wrapperClass="sm:col-span-2"
              />
            </div>

            <p className="mt-6 mb-3 text-sm font-medium text-foreground-900">Signature des certificats et des cartes</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Fonction du signataire"
                placeholder="Le Directeur"
                value={info.signatoryTitle}
                onChange={(e) => setInfo({ ...info, signatoryTitle: e.target.value })}
              />
              <Input
                label="Nom du signataire"
                placeholder="M. Mamadou Camara"
                value={info.signatoryName}
                onChange={(e) => setInfo({ ...info, signatoryName: e.target.value })}
              />
            </div>

            <CurrencyFields info={info} onChange={(patch) => setInfo({ ...info, ...patch })} />
            <div className="mt-5 flex justify-end">
              <Button
                icon="ri-save-3-line"
                onClick={async () => {
                  if (!info.currencySymbol.trim() || !info.currencyName.trim() || !info.currencyNamePlural.trim()) {
                    notify("Indiquez le symbole et le nom (singulier et pluriel) de la devise.", "error");
                    return;
                  }
                  setInfo(await updateSchoolInfo(info));
                  // Barre latérale et en-tête : nom et logo à jour immédiatement.
                  await refreshSchools();
                  notify("Informations de l'école enregistrées.");
                }}
              >
                Enregistrer
              </Button>
            </div>
          </Card>
          <Card>
            <SectionTitle icon="ri-vip-crown-line" title="Abonnement" subtitle="Géré par WaitechEdu" />
            <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-xs text-foreground-500">Forfait</dt>
                <dd className="mt-0.5 font-medium text-foreground-900">{PLAN_LABEL[info.plan]}</dd>
                <dd className="mt-0.5 text-xs text-foreground-500">{PLAN_SUMMARY[info.plan]}</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">Fin de l'abonnement</dt>
                <dd className="mt-0.5 font-medium text-foreground-900">
                  {info.subscriptionEndsAt
                    ? new Date(`${info.subscriptionEndsAt}T00:00:00`).toLocaleDateString("fr-FR")
                    : "Sans échéance"}
                </dd>
                {info.subscriptionEndsAt && <SubscriptionRemaining endsAt={info.subscriptionEndsAt} />}
              </div>
            </dl>
            <p className="mt-4 text-xs text-foreground-500">
              Pour changer de forfait ou prolonger l'abonnement, contactez WaitechEdu.
            </p>
          </Card>
          </>
        )}

        {active === "cycles" && <CyclesPanel />}
        {active === "years" && <SchoolYearsPanel />}
        {active === "grading" && showGrading && <GradingPanel />}
      </div>
    </div>
  );
}
/** Temps restant avant la fin de l'abonnement, en orange dans les 30 derniers jours, en rouge une fois échu. */
function SubscriptionRemaining({ endsAt }: { endsAt: string }) {
  const { expired, daysLeft } = subscriptionState(endsAt);
  if (daysLeft === null) return null;
  const tone = expired
    ? "text-accent-700"
    : daysLeft <= SUBSCRIPTION_WARNING_DAYS
      ? "text-warning-700"
      : "text-foreground-500";
  const text = expired
    ? "Abonnement terminé : consultation seule"
    : daysLeft === 0
      ? "Dernier jour aujourd'hui"
      : `Il reste ${plural(daysLeft, "jour")}`;
  return <dd className={`mt-0.5 text-xs font-medium ${tone}`}>{text}</dd>;
}
